import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `https://hr.qfinitymedia.co.za/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('qfinity_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('qfinity_token');
      localStorage.removeItem('qfinity_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
};

// Employees
export const employeeAPI = {
  getAll: () => api.get('/employees'),
  getOne: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/employees/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Governance
export const governanceAPI = {
  getDocuments: () => api.get('/governance/documents'),
  getDocument: (id) => api.get(`/governance/documents/${id}`),
};

// Timesheets
export const timesheetAPI = {
  getAll: () => api.get('/timesheets'),
  getPending: () => api.get('/timesheets/pending'),
  create: (data) => api.post('/timesheets', data),
  approve: (id) => api.put(`/timesheets/${id}/approve`),
};

// Performance Reviews
export const performanceAPI = {
  getAll: () => api.get('/performance-reviews'),
  create: (data) => api.post('/performance-reviews', data),
};

// Disciplinary
export const disciplinaryAPI = {
  getAll: () => api.get('/disciplinary-records'),
  getByEmployee: (id) => api.get(`/disciplinary-records/${id}`),
  create: (data) => api.post('/disciplinary-records', data),
};

// Contracts
export const contractAPI = {
  getAll: () => api.get('/contracts'),
  getByEmployee: (id) => api.get(`/contracts/${id}`),
};

// Recruitment
export const recruitmentAPI = {
  getPositions: () => api.get('/recruitment/positions'),
  apply: (data) => api.post('/recruitment/apply', data),
};

// Branch Transfers
export const transferAPI = {
  getAll: () => api.get('/branch-transfers'),
  getPending: () => api.get('/branch-transfers/pending'),
  create: (data) => api.post('/branch-transfers', data),
  approve: (id) => api.put(`/branch-transfers/${id}/approve`),
};

// Company News
export const newsAPI = {
  getAll: () => api.get('/company-news'),
  create: (data) => api.post('/company-news', data),
};

// Monitoring
export const monitoringAPI = {
  updateLocation: (data) => api.post('/monitoring/location', data),
  getAllLocations: () => api.get('/monitoring/locations'),
  getMapData: () => api.get('/monitoring/map-data'),
  getEmployeeLocations: (id) => api.get(`/monitoring/locations/${id}`),
  logActivity: (data) => api.post('/monitoring/activity', data),
  getEmployeeActivity: (id) => api.get(`/monitoring/activity/${id}`),
  getLoginLogs: () => api.get('/monitoring/login-logs'),
  getVPNAlerts: () => api.get('/monitoring/vpn-alerts'),
  getScreenRecordings: () => api.get('/monitoring/screen-recordings'),
};

// Payroll
export const payrollAPI = {
  getAll: () => api.get('/payroll'),
  getByEmployee: (id) => api.get(`/payroll/${id}`),
  create: (data) => api.post('/payroll', data),
  updateStatus: (id, status) => api.put(`/payroll/${id}/status`, { status }),
};

// Training
export const trainingAPI = {
  getModules: () => api.get('/training/modules'),
  getProgress: (id) => api.get(`/training/progress/${id}`),
  updateProgress: (data) => api.post('/training/progress', data),
};

// KPI
export const kpiAPI = {
  getByEmployee: (id) => api.get(`/kpi/${id}`),
  getCompanyOverview: () => api.get('/kpi/company/overview'),
  create: (data) => api.post('/kpi', data),
};

// Health
export const healthAPI = {
  getByEmployee: (id) => api.get(`/health/${id}`),
  create: (data) => api.post('/health', data),
};

// Leave
export const leaveAPI = {
  getAll: () => api.get('/leave'),
  getPending: () => api.get('/leave/pending'),
  getByEmployee: (id) => api.get(`/leave/${id}`),
  create: (data) => api.post('/leave', data),
  approve: (id) => api.put(`/leave/${id}/approve`),
  reject: (id) => api.put(`/leave/${id}/reject`),
};

// Overtime
export const overtimeAPI = {
  getAll: () => api.get('/overtime'),
  getPending: () => api.get('/overtime/pending'),
  create: (data) => api.post('/overtime', data),
  approve: (id) => api.put(`/overtime/${id}/approve`),
};

// Assessment
export const assessmentAPI = {
  getQuestions: () => api.get('/assessment/questions'),
  submit: (data) => api.post('/assessment/submit', data),
  getHistory: (id) => api.get(`/assessment/history/${id}`),
};

// AI
export const aiAPI = {
  analyzeEmployee: (id) => api.get(`/ai/analyze/${id}`),
  getCompanyInsights: () => api.get('/ai/company-insights'),
};

// Audit
export const auditAPI = {
  getLogs: () => api.get('/audit/logs'),
  verifyBlock: (id) => api.get(`/audit/verify/${id}`),
};

// Dashboard
export const dashboardAPI = {
  getEmployeeDashboard: (id) => api.get(`/dashboard/employee/${id}`),
  getHRDashboard: () => api.get('/dashboard/hr'),
  getExecutiveDashboard: () => api.get('/dashboard/executive'),
};

// HR Finance
export const hrFinanceAPI = {
  getDashboard: () => api.get('/hr-finance/dashboard'),
  getSalaryBreakdown: () => api.get('/hr-finance/salary-breakdown'),
};

export default api;
